import { put, select, takeLatest } from 'redux-saga/effects';
import { SET_LOCALE, SET_MENU_SIZE } from '../constants';
import { APP_LOCALE, APP_MENU_SIZE } from '../reducer';
import { SetLocaleAction, SetMenuSize } from '../types';
import { PreferenceSections, Preferences } from 'utils/preferences';
import { getAuthUser } from 'store/auth/selectors';
import { updateCurrentUserPreferencesRequest } from 'store/auth/actions';
import { User } from 'store/auth/models';

function parseUserPreferences(currentUser?: User): Preferences[] {
  const rawStoredPrefs = currentUser?.prefDetails?.[1];
  if (!rawStoredPrefs) return [];
  if (typeof rawStoredPrefs === 'string') {
    try {
      return JSON.parse(rawStoredPrefs);
    } catch (error) {
      console.warn('Unable to parse stored preferences', error);
      return [];
    }
  }
  if (Array.isArray(rawStoredPrefs)) {
    return rawStoredPrefs;
  }
  return [];
}

function upsertAppPreference(
  allPreferences: Preferences[],
  pref: { name: string; value: unknown }
): boolean {
  const section = PreferenceSections.APP;
  let sIdx = allPreferences.findIndex((p) => p.section === section);
  if (sIdx === -1) {
    allPreferences.push({ section, prefs: [pref] });
    return true;
  }
  const pIdx = allPreferences[sIdx].prefs.findIndex(
    (p) => p.name === pref.name
  );
  if (pIdx === -1) {
    allPreferences[sIdx].prefs.push(pref);
    return true;
  }
  if (allPreferences[sIdx].prefs[pIdx].value === pref.value) {
    return false;
  }
  allPreferences[sIdx].prefs[pIdx] = pref;
  return true;
}

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* setMenuSizeSaga(action: SetMenuSize) {
  const { menuSize, userUuid } = action.payload;

  if (!userUuid) {
    return;
  }

  const currentUser: User | undefined = yield select(getAuthUser);
  if (!currentUser) return;

  const allPreferences = parseUserPreferences(currentUser);
  const changed = upsertAppPreference(allPreferences, {
    name: APP_MENU_SIZE,
    value: menuSize,
  });
  if (!changed) return;

  yield put(updateCurrentUserPreferencesRequest(allPreferences));
}

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* setLocaleSaga(action: SetLocaleAction) {
  const { locale, userUuid } = action.payload;

  if (!userUuid) {
    return;
  }

  const currentUser: User | undefined = yield select(getAuthUser);
  if (!currentUser) return;

  const allPreferences = parseUserPreferences(currentUser);
  const changed = upsertAppPreference(allPreferences, {
    name: APP_LOCALE,
    value: locale,
  });
  if (!changed) return;

  yield put(updateCurrentUserPreferencesRequest(allPreferences));
}

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* appSaga() {
  yield takeLatest(SET_MENU_SIZE, setMenuSizeSaga);
  yield takeLatest(SET_LOCALE, setLocaleSaga);
}

